import { z } from 'zod';

import { zodContractPayloadBaseSchema } from '~/data/contract/validations';

import {
  CONTRACT_IMPORT_JOB_ACTION,
  CONTRACT_IMPORT_JOB_FAILURE_TYPE,
  CONTRACT_IMPORT_JOB_STATUS,
} from './constants';

/**
 * Shared validation schemas for contract-import-job-related API calls.
 */

// One file attached to an import job, as listed on the detail response.
export const zodContractImportJobFileInfoSchema = z.object({
  fileId: z.uuid(),
  fileMime: z.string(),
  fileName: z.string(),
  sasUrl: z.url().nullish(),
});

// Lightweight job representation used in list responses.
export const zodContractImportJobSummarySchema = z.object({
  availableActions: z.array(z.enum(CONTRACT_IMPORT_JOB_ACTION)),
  createdOn: z.iso.datetime({ offset: true }),
  errorMessage: z.string().nullish(),
  failureType: z.enum(CONTRACT_IMPORT_JOB_FAILURE_TYPE).nullish(),
  fileName: z.string(),
  id: z.uuid(),
  sasUrl: z.url().nullish(),
  status: z.enum(CONTRACT_IMPORT_JOB_STATUS),
});

// Unconstrained bag of extra OCR fields the backend does not yet model.
const zodExtractedAdditionalDataSchema = z.looseObject({}).nullish();

// One delivery/party address as extracted by AI/OCR, pre-review. Country is
// an unconstrained string (not ISO-3166 alpha-2) because the value has not
// been normalized yet.
const zodExtractedAddressSchema = z.object({
  additionalData: zodExtractedAdditionalDataSchema,
  additionalInfo: z.string().nullish(),
  buildingNumber: z.string().nullish(),
  city: z.string().nullish(),
  country: z.string().nullish(),
  lineOne: z.string().nullish(),
  postCode: z.string().nullish(),
  streetName: z.string().nullish(),
});

const zodExtractedContactSchema = z.object({
  additionalData: zodExtractedAdditionalDataSchema,
  email: z.string().nullish(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  party: z.string().nullish(),
  phone: z.string().nullish(),
  role: z.string().nullish(),
});

const zodExtractedPaymentTermSchema = z.object({
  actualAmount: z.string().nullish(),
  additionalData: zodExtractedAdditionalDataSchema,
  basisAmount: z.string().nullish(),
  dueAmount: z.string().nullish(),
  dueDate: z.string().nullish(),
  duePeriod: z.string().nullish(),
  duePeriodUnit: z.string().nullish(),
  percentage: z.string().nullish(),
});

// One line item as extracted by AI/OCR, pre-normalization: `unitType` may
// still be a document alias (e.g. "Stück") rather than a UN/CEFACT code, and
// `grossPrice`/`quantityOrdered` may use a locale decimal separator.
const zodExtractedItemSchema = z.object({
  additionalData: zodExtractedAdditionalDataSchema,
  buyerArticleId: z.string().nullish(),
  grossPrice: z.string().nullish(),
  grossPricePerQuantity: z.number().int().min(1).nullish(),
  manufacturer: z.string().nullish(),
  notes: z.string().nullish(),
  position: z.number().int().min(1).nullish(),
  productDescription: z.string().nullish(),
  productName: z.string().nullish(),
  quantityOrdered: z.string().nullish(),
  sellerArticleId: z.string().nullish(),
  unitType: z.string().nullish(),
  unitTypeRaw: z.string().nullish(),
});

// AI/OCR-extracted contract fields, shaped to mirror ContractBase. Every
// field is nullish so a partial extraction still parses.
export const zodContractImportJobExtractedDataSchema = z.object({
  additionalData: zodExtractedAdditionalDataSchema,
  availabilityDate: z.string().nullish(),
  buyerAddress: zodExtractedAddressSchema.nullish(),
  buyerAssignedId: z.string().nullish(),
  buyerCompanyName: z.string().nullish(),
  contacts: z.array(zodExtractedContactSchema).nullish(),
  contractDate: z.string().nullish(),
  contractName: z.string().nullish(),
  currency: z.string().nullish(),
  deliveryAddresses: z.array(zodExtractedAddressSchema).nullish(),
  deliveryTerms: z.string().nullish(),
  incoterm: z.string().nullish(),
  items: z.array(zodExtractedItemSchema).nullish(),
  notes: z.string().nullish(),
  paymentTerms: z.array(zodExtractedPaymentTermSchema).nullish(),
  sellerAddress: zodExtractedAddressSchema.nullish(),
  sellerAssignedId: z.string().nullish(),
  sellerCompanyId: z.uuid().nullish(),
  sellerCompanyName: z.string().nullish(),
  validFrom: z.string().nullish(),
  validUntil: z.string().nullish(),
});

// Full job details, including the OCR-extracted data and available actions.
export const zodContractImportJobDetailSchema =
  zodContractImportJobSummarySchema.extend({
    availableActions: z.array(z.enum(CONTRACT_IMPORT_JOB_ACTION)),
    contractId: z.uuid().nullish(),
    errorMessage: z.string().nullish(),
    extractedData: zodContractImportJobExtractedDataSchema.nullish(),
    extractionWarnings: z.array(z.string()).nullish(),
    failureType: z.enum(CONTRACT_IMPORT_JOB_FAILURE_TYPE).nullish(),
    files: z.array(zodContractImportJobFileInfoSchema),
  });

// Paginated list response wrapping import job summaries.
export const zodPaginatedContractImportJobsResponseSchema = z.object({
  data: z.array(zodContractImportJobSummarySchema),
  hasNextPage: z.boolean(),
  paginatedCount: z.number().int().nonnegative(),
  totalCount: z.number().int().nonnegative().nullish(),
});

// One reviewed contract line item, still in raw (pre-normalization) form:
// `unitType` is whatever alias the source document/OCR used (e.g. "Stück")
// rather than a resolved UN/CEFACT code, and `grossPrice`/`quantityOrdered`
// are plain strings that may use a locale decimal separator (e.g. "1.234,56").
export const zodImportedContractItemSchema = z.object({
  buyerArticleId: z.string().nullish(),
  grossPrice: z.string(),
  grossPricePerQuantity: z.number().int().min(1).nullish(),
  manufacturer: z.string().nullish(),
  notes: z.string().nullish(),
  position: z.number().int().min(1).nullish(),
  productDescription: z.string().nullish(),
  productName: z.string(),
  quantityOrdered: z.string(),
  sellerArticleId: z.string().nullish(),
  unitType: z.string(),
});

// Contract-creation payload for the "create new contract" import path.
// Identical to the regular create-contract payload except items carry raw,
// unnormalized values (see zodImportedContractItemSchema).
export const zodNewContractFromImportSchema =
  zodContractPayloadBaseSchema.extend({
    items: z.array(zodImportedContractItemSchema).min(1),
  });
